import { PITCH_OPTIONS, WASTE_PCT_OPTIONS } from "@roof-hawk/core";

import { t, tn } from "../../i18n";

/**
 * A ticked roof with no pitch, which cannot be saved: the roof row needs one.
 * Hover's pitch is not always one of ours, and such a roof used to be dropped
 * without a word while the walls went in (#623). So the box asks for the
 * pitch, and the wall form's Submit waits for it or for the box to be
 * unticked.
 */
export function roofWithoutPitch(
  roof: { pitch: string } | null,
  save: boolean,
): boolean {
  return save && roof !== null && roof.pitch === "";
}

/** The roof off the same report, offered as one ticked box. */
export function RoofOffer({
  roof,
  measured,
  save,
  onSave,
  onPitch,
  waste,
  onWaste,
}: {
  roof: { pitch: string; count: number };
  /** Roof layouts the job already has. */
  measured: number;
  save: boolean;
  onSave: (on: boolean) => void;
  onPitch: (pitch: string) => void;
  waste: string;
  onWaste: (waste: string) => void;
}) {
  return (
    <div className="config-warn" role="status" style={{ margin: "8px 0" }}>
      <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="checkbox"
          checked={save}
          onChange={(e) => onSave(e.target.checked)}
        />
        <span>
          <strong>{t("Also save the roof measurements")}</strong>{" "}
          {t(
            "read off this report ({count} read{pitch}) onto the Roof Measurements card.",
            {
              count: roof.count,
              pitch: roof.pitch
                ? t(", {pitch} pitch", { pitch: roof.pitch })
                : "",
            },
          )}
        </span>
      </label>
      {measured > 0 && (
        <div className="na-hint" style={{ marginTop: 6 }}>
          {t(
            "Left unticked because this job already has {what}. A roof estimate adds up every one on the job, so saving this roof too would count it twice. Tick the box only if it is a roof not measured yet.",
            {
              what: tn(measured, "a roof measurement", "{n} roof measurements"),
            },
          )}
        </div>
      )}
      {save && (
        <div className="fgrid" style={{ marginTop: 8 }}>
          <div className="field">
            <label>{t("Roof pitch")}</label>
            <select
              aria-label={t("Roof pitch")}
              value={roof.pitch}
              onChange={(e) => onPitch(e.target.value)}
            >
              <option value="">{t("Pitch")}</option>
              {PITCH_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            {roof.pitch === "" && (
              <div className="err" role="alert" style={{ marginTop: 6 }}>
                {t(
                  "Pick the roof pitch, or untick the box to save the walls alone.",
                )}
              </div>
            )}
          </div>
          <div className="field">
            <label>{t("Roof waste pct")}</label>
            <select
              aria-label={t("Roof waste pct")}
              value={waste}
              onChange={(e) => onWaste(e.target.value)}
            >
              {WASTE_PCT_OPTIONS.map((w) => (
                <option key={w} value={w}>
                  {w}%
                </option>
              ))}
            </select>
            <span className="na-hint">
              {t("Hover recommends none, so this is yours to pick.")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
